/**
 * ServicesPage - Gestione Servizi
 * Stessa logica di ProductsPage ma filtrata per item_type="service"
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
  Briefcase,
  Plus,
  Pencil,
  Trash2,
  Search,
  MoreVertical,
  Grid3X3,
  List,
  BookOpen,
  MousePointerClick,
  Lightbulb,
  ArrowRight,
  HelpCircle,
  Loader2,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Service {
  id: string;
  name: string;
  description?: string;
  price: number;
  unit: string;
  category?: string;
  sku?: string;
  image_url?: string;
  is_active: boolean;
  item_type: string;
}

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { confirm: confirmAction, ConfirmDialog } = useConfirmDialog();
  
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', price: '', unit: 'ora', category: '', sku: '', is_active: true
  });
  
  // Tutorial
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  const unitOptions = [
    { value: 'ora', label: 'Ora' },
    { value: 'sessione', label: 'Sessione' },
    { value: 'giorno', label: 'Giorno' },
    { value: 'mese', label: 'Mese' },
    { value: 'intervento', label: 'Intervento' },
    { value: 'pz', label: 'Pezzo' },
    { value: 'forfait', label: 'Forfait' },
  ];

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products?item_type=service`);
      setServices(Array.isArray(res.data) ? res.data : res.data.products || []);
    } catch (error) {
      toast.error('Errore nel caricamento dei servizi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const filteredServices = services.filter(s =>
    !searchQuery ||
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openDialog = (service?: Service) => {
    if (service) {
      setEditingService(service);
      setForm({
        name: service.name, description: service.description || '',
        price: (service.price ?? '').toString(), unit: service.unit || 'ora',
        category: service.category || '', sku: service.sku || '',
        is_active: service.is_active ?? true
      });
    } else {
      setEditingService(null);
      setForm({ name: '', description: '', price: '', unit: 'ora', category: '', sku: '', is_active: true });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Il nome è obbligatorio'); return; }
    setSaving(true);
    try {
      const priceValue = form.price ? parseFloat(form.price) : null;
      const data = { ...form, price: priceValue, item_type: 'service', unit_of_measure: form.unit };
      if (editingService) {
        await axios.put(`${API}/products/${editingService.id}`, data);
        toast.success('Servizio aggiornato');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Servizio creato');
      }
      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (service: Service) => {
    if (!await confirmAction({ title: 'Elimina servizio', description: `Eliminare "${service.name}"?`, confirmText: 'Elimina', variant: 'destructive' })) return;
    try {
      await axios.delete(`${API}/products/${service.id}`);
      toast.success('Servizio eliminato');
      fetchServices();
    } catch (error) { toast.error('Errore'); }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
      </div>
    );
  }

  // Empty state
  if (services.length === 0) {
    return (
      <div className="p-6 space-y-8 max-w-3xl mx-auto" data-testid="services-page">
        <ConfirmDialog />
        <div className="text-center pt-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
            <Briefcase className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-3">I tuoi Servizi</h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Qui gestisci i servizi che offri ai tuoi clienti: consulenze, formazione, assistenza, manutenzione e tutto ciò che non è un prodotto fisico.
          </p>
        </div>
        <div className="flex justify-center">
          <Card className="group cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all max-w-sm w-full" onClick={() => openDialog()}>
            <CardContent className="p-6 text-center space-y-3">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-blue-50 dark:bg-blue-950/30">
                <Plus className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="font-semibold">Aggiungi il primo servizio</h3>
              <p className="text-sm text-muted-foreground">Inserisci nome, prezzo e unità di misura</p>
            </CardContent>
          </Card>
        </div>
        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Nuovo Servizio</DialogTitle></DialogHeader>
            {renderForm()}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  function renderForm() {
    return (
      <>
        <div className="space-y-4">
          <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Es. Consulenza HACCP" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1.5">
                Prezzo
                <span className="text-xs text-muted-foreground font-normal">(opzionale)</span>
              </Label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} placeholder="Es. 120.00 — lascia vuoto se da concordare" />
              <p className="text-xs text-muted-foreground mt-1">Puoi impostarlo in un secondo momento o definirlo per singolo cliente tramite listino.</p>
            </div>
            <div><Label>Unità</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{unitOptions.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Codice</Label><Input value={form.sku} onChange={(e) => setForm({...form, sku: e.target.value})} placeholder="SRV-001" /></div>
            <div><Label>Categoria</Label><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} placeholder="Es. Formazione" /></div>
          </div>
          <div><Label>Descrizione</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Descrivi il servizio..." rows={2} /></div>
          <div className="flex items-center justify-between"><Label>Servizio attivo</Label><Switch checked={form.is_active} onCheckedChange={(v) => setForm({...form, is_active: v})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editingService ? 'Salva' : 'Crea'}</Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="services-page">
      <ConfirmDialog />
      
      {/* Banner intro */}
      <div className="border-b bg-gradient-to-br from-primary/5 via-background to-blue-50/50 dark:from-primary/10 dark:via-background dark:to-blue-950/10">
        <div className="px-6 py-5 max-w-5xl">
          <div className="flex items-start gap-4">
            <div className="bg-primary/10 rounded-xl p-3 shrink-0"><Briefcase className="h-7 w-7 text-primary" /></div>
            <div className="flex-1 space-y-2">
              <h2 className="text-lg font-semibold">I tuoi Servizi</h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
                Gestisci i servizi che offri: consulenze, formazione, assistenza tecnica, manutenzione. I tuoi clienti potranno consultarli e richiederli dalla piattaforma.
              </p>
              <Button size="sm" onClick={() => { setTutorialStep(0); setShowTutorial(true); }} className="gap-2">
                <MousePointerClick className="h-4 w-4" /> Scopri come funziona
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b bg-background space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Servizi</h1>
            <p className="text-sm text-muted-foreground">{services.length} servizi</p>
          </div>
          <Button onClick={() => openDialog()}><Plus className="h-4 w-4 mr-2" /> Nuovo Servizio</Button>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Cerca servizi..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
          </div>
          <div className="flex items-center border rounded-lg shrink-0">
            <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="icon" className="rounded-r-none" onClick={() => setViewMode('grid')}><Grid3X3 className="h-4 w-4" /></Button>
            <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="icon" className="rounded-l-none" onClick={() => setViewMode('list')}><List className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {/* Lista */}
      <ScrollArea className="flex-1">
        {filteredServices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Briefcase className="h-12 w-12 mb-4" /><p className="font-medium">Nessun servizio trovato</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="p-4">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Servizio</TableHead><TableHead>Codice</TableHead><TableHead>Categoria</TableHead>
                <TableHead className="text-right">Prezzo</TableHead><TableHead>Stato</TableHead><TableHead className="w-12"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filteredServices.map(svc => (
                  <TableRow key={svc.id}>
                    <TableCell><div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-50 dark:bg-blue-950/30 rounded flex items-center justify-center"><Briefcase className="h-5 w-5 text-blue-500" /></div><span className="font-medium">{svc.name}</span></div></TableCell>
                    <TableCell className="text-muted-foreground">{svc.sku || '-'}</TableCell>
                    <TableCell>{svc.category || '-'}</TableCell>
                    <TableCell className="text-right font-medium">{svc.price ? `${formatCurrency(svc.price)}/${svc.unit}` : <span className="text-muted-foreground italic text-sm">Da concordare</span>}</TableCell>
                    <TableCell><Badge variant={svc.is_active ? 'default' : 'secondary'}>{svc.is_active ? 'Attivo' : 'Inattivo'}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDialog(svc)}><Pencil className="h-4 w-4 mr-2" />Modifica</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(svc)}><Trash2 className="h-4 w-4 mr-2" />Elimina</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredServices.map(svc => (
              <Card key={svc.id} className="group hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-blue-50 dark:bg-blue-950/30 rounded-lg flex items-center justify-center"><Briefcase className="h-5 w-5 text-blue-500" /></div>
                    <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openDialog(svc)}><Pencil className="h-4 w-4 mr-2" />Modifica</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(svc)}><Trash2 className="h-4 w-4 mr-2" />Elimina</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <h3 className="font-medium">{svc.name}</h3>
                  {svc.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{svc.description}</p>}
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-bold text-primary">{svc.price ? `${formatCurrency(svc.price)}/${svc.unit}` : <span className="text-muted-foreground italic text-sm">Prezzo da concordare</span>}</span>
                    {!svc.is_active && <Badge variant="secondary">Inattivo</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Dialog Servizio */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingService ? 'Modifica Servizio' : 'Nuovo Servizio'}</DialogTitle></DialogHeader>
          {renderForm()}
        </DialogContent>
      </Dialog>

      {/* Tutorial */}
      <Dialog open={showTutorial} onOpenChange={setShowTutorial}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lightbulb className="h-5 w-5 text-primary" />{tutorialStep === 0 ? "Come funzionano i Servizi" : "Prezzi e listini"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {tutorialStep === 0 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center"><Briefcase className="h-12 w-12 text-primary mx-auto mb-3" /><p className="font-medium text-lg">I tuoi servizi digitali</p></div>
                <p className="text-sm text-muted-foreground leading-relaxed">I servizi funzionano come i prodotti: li inserisci con nome, prezzo e descrizione. I clienti li vedranno nel catalogo e potranno richiederli.</p>
                <p className="text-sm text-muted-foreground leading-relaxed">Esempi: consulenze, formazione, assistenza tecnica, manutenzione programmata, servizi di logistica.</p>
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3"><p className="text-sm text-green-800 dark:text-green-300"><strong>L'unità di misura</strong> può essere ora, sessione, giorno, intervento o forfait.</p></div>
              </div>
            )}
            {tutorialStep === 1 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center"><Briefcase className="h-12 w-12 text-primary mx-auto mb-3" /><p className="font-medium text-lg">Prezzi e listini</p></div>
                <p className="text-sm text-muted-foreground leading-relaxed">I servizi usano gli stessi listini dei prodotti. Puoi creare listini personalizzati con prezzi diversi per singoli clienti o associazioni.</p>
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3"><p className="text-sm text-blue-800 dark:text-blue-300"><strong>I servizi e i prodotti</strong> possono coesistere nello stesso catalogo e negli stessi ordini.</p></div>
              </div>
            )}
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <div className="flex items-center gap-1.5">{[0,1].map(i => (<button key={i} onClick={() => setTutorialStep(i)} className={cn("w-2 h-2 rounded-full transition-all", tutorialStep === i ? "bg-primary w-6" : "bg-muted-foreground/30")} />))}</div>
            <div className="flex items-center gap-2">
              {tutorialStep > 0 && <Button variant="ghost" size="sm" onClick={() => setTutorialStep(0)}>Indietro</Button>}
              {tutorialStep < 1 ? <Button size="sm" onClick={() => setTutorialStep(1)}>Avanti<ArrowRight className="h-4 w-4 ml-1" /></Button> : <Button size="sm" onClick={() => setShowTutorial(false)}>Ho capito!</Button>}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesPage;
